import { useCallback, useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import Pagination from './Pagination';
import apiService from '../services/api';

const PAGE_SIZE = 100;

const SORT_FIELD_MAP = {
  name: 'name',
  email: 'email',
  school: 'school',
  role: 'role',
};

const cell = {
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  minWidth: 0,
  flexShrink: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  paddingRight: '12px',
};

const headerCell = {
  ...cell,
  fontWeight: 900,
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
};

function displayName(user) {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email;
}

function roleLabel(user) {
  if (user.is_superuser) return 'Superuser';
  if (user.is_staff) return 'Staff';
  if (user.is_merchant_specialist) return 'Merchant Specialist';
  if (user.is_embed_tester) return 'Embed Tester';
  return 'User';
}

function orderingParam(sortConfig) {
  if (!sortConfig?.key) return '-created_at';
  const field = SORT_FIELD_MAP[sortConfig.key];
  if (!field) return '-created_at';
  return sortConfig.direction === 'desc' ? `-${field}` : field;
}

const UserPickerModal = ({
  isOpen,
  onClose,
  excludeIds = [],
  onConfirm,
  confirming = false,
  title = 'Add people',
}) => {
  const listRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  const excluded = new Set((excludeIds || []).map((id) => String(id)));
  const selectedCount = Object.keys(selected).length;

  useEffect(() => {
    if (!isOpen) return undefined;
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [isOpen, searchTerm]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setDebouncedSearch('');
    setPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setSelected({});
  }, [isOpen]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        ordering: orderingParam(sortConfig),
      };
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await apiService.getAllUsersSuperuser(params);
      setUsers(response.users || []);
      setTotalCount(response.count ?? 0);
    } catch (err) {
      console.error('Failed to load users:', err);
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sortConfig]);

  useEffect(() => {
    if (!isOpen) return;
    fetchUsers();
  }, [isOpen, fetchUsers]);

  if (!isOpen) return null;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setPage(1);
    setSortConfig({ key, direction });
  };

  const goToPage = (next) => {
    setPage(next);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const toggle = (user) => {
    if (excluded.has(String(user.id))) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[user.id]) delete next[user.id];
      else next[user.id] = user;
      return next;
    });
  };

  const confirm = () => {
    const picked = Object.values(selected);
    if (!picked.length || confirming) return;
    onConfirm(picked);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        zIndex: 1002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !confirming) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-picker-title"
        style={{
          width: '100%',
          maxWidth: '1100px',
          maxHeight: '90vh',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 18px',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0,
          }}
        >
          <div
            id="user-picker-title"
            style={{
              fontFamily: '"Baikal Book"',
              fontSize: '16px',
              fontWeight: 900,
              color: '#000',
            }}
          >
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: confirming ? 'not-allowed' : 'pointer',
              fontSize: '20px',
              lineHeight: 1,
              padding: '6px 8px',
              borderRadius: '8px',
              color: '#111827',
              opacity: confirming ? 0.6 : 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '16px 18px 0', flexShrink: 0 }}>
          <input
            type="text"
            placeholder="Search users . . ."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: '"Baikal Book"',
            }}
          />
        </div>

        <div
          style={{
            padding: '0 18px',
            marginTop: '16px',
            flexShrink: 0,
            display: 'flex',
            height: '45px',
            alignItems: 'center',
            borderRadius: '12px 12px 0 0',
            background: '#F8F8F8',
          }}
        >
          <div style={{ width: '36px', flexShrink: 0 }} />
          <div onClick={() => handleSort('name')} style={{ ...headerCell, width: '24%' }}>
            Name
            <Icon name="up-down" size={12} />
          </div>
          <div onClick={() => handleSort('email')} style={{ ...headerCell, width: '32%' }}>
            Email
            <Icon name="up-down" size={12} />
          </div>
          <div onClick={() => handleSort('school')} style={{ ...headerCell, width: '26%' }}>
            School
            <Icon name="up-down" size={12} />
          </div>
          <div onClick={() => handleSort('role')} style={{ ...headerCell, width: '18%' }}>
            Role
            <Icon name="up-down" size={12} />
          </div>
        </div>

        <Pagination
          position="top"
          page={page}
          totalItems={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={goToPage}
          scrollOnChange={false}
        />

        <div ref={listRef} style={{ flex: '1 1 auto', overflow: 'auto', padding: '0 18px 8px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading users...</div>
          ) : users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>No users found.</div>
          ) : (
            users.map((user, index) => {
              const already = excluded.has(String(user.id));
              const checked = already || Boolean(selected[user.id]);
              return (
                <label
                  key={user.id || index}
                  style={{
                    display: 'flex',
                    width: '100%',
                    minHeight: '59px',
                    padding: '12px 18px',
                    alignItems: 'center',
                    borderRadius: '12px',
                    background: checked && !already ? '#e8f8ff' : '#F8F8F8',
                    marginTop: '8px',
                    cursor: already ? 'default' : 'pointer',
                    opacity: already ? 0.55 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={already || confirming}
                    onChange={() => toggle(user)}
                    style={{ width: 15, height: 15, marginRight: 16, flexShrink: 0, cursor: already ? 'default' : 'pointer' }}
                  />
                  <div style={{ ...cell, width: '24%' }}>{displayName(user)}</div>
                  <div style={{ ...cell, width: '32%', opacity: 0.7 }}>{user.email}</div>
                  <div
                    style={{
                      ...cell,
                      width: '26%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      paddingLeft: user.active_school?.image ? '4px' : 0,
                    }}
                    title={user.active_school?.name || ''}
                  >
                    {user.active_school?.image ? (
                      <img
                        src={user.active_school.image}
                        alt=""
                        style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'contain', flexShrink: 0 }}
                      />
                    ) : (
                      <span>{user.active_school?.name || 'No School'}</span>
                    )}
                  </div>
                  <div
                    style={{
                      ...cell,
                      width: '18%',
                      fontWeight: user.is_superuser ? 'bold' : 'normal',
                      color: user.is_superuser
                        ? '#dc2626'
                        : (user.is_staff ? '#2563eb' : (user.is_merchant_specialist ? '#0f766e' : (user.is_embed_tester ? '#7c3aed' : 'inherit'))),
                    }}
                  >
                    {already ? 'On list' : roleLabel(user)}
                  </div>
                </label>
              );
            })
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '14px 18px',
            borderTop: '1px solid #e5e7eb',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            className="dashboard-button"
            onClick={onClose}
            disabled={confirming}
            style={{ minWidth: 0, padding: '8px 16px' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="staff-page-button"
            onClick={confirm}
            disabled={confirming || selectedCount === 0}
          >
            <span className="staff-page-button-text">
              {confirming
                ? 'Saving...'
                : selectedCount === 1
                  ? 'Add 1 person'
                  : `Add ${selectedCount} people`}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPickerModal;
